import React from 'react';
import I18n from 'i18n-js';
import bind from '@reverbdotcom/commons/src/bind';
import UrlHelpers from './shared/url_helpers';
import { ChildProps } from 'react-apollo';
import gql from 'graphql-tag';

import CSPReview from './csp_review';
import CSPReviewButton from './csp_review_button';

import Tiles from '@reverbdotcom/commons/src/components/tiles';
import { RCStarRating, RCAlertBox } from '@reverbdotcom/cadence/components';
import RQLPagination from '@reverbdotcom/commons/src/components/rql_pagination';
import { ReviewMetaData } from '@reverbdotcom/commons/src/cms/lib/metadata_helper';

import {
  core_apimessages_CSPReview as ProductReview,
  CoreCspReviews,
  CSPReviewCSP,
  CSPReviews as IReviews,
} from '../schemas/rql';

import CoreClient from '../lib/core_client';

interface ICSPWithReview extends CSPReviewCSP.Fragment {
  reviews?: IReviews.Fragment;
}

interface IProps {
  title?: string;
  csp: ICSPWithReview;
  name?: string;
  image?: string;
  offset?: number;
  // on the item show page, rendered inside a component that already shows the stars
  hideHeading?: boolean;
  truncateAllReviews?: boolean;
  refetch: () => void;
  inCspCtaSidebarExperiment?: boolean;
  trackingDisplayStyle: 'product' | 'csp';
}

interface IState {
  reviewSubmittedSuccess: boolean;
}

const REVIEWS_LIMIT = 5;

export class CSPReviews extends React.Component<ChildProps<IProps, CoreCspReviews.Query>, IState> {
  state = {
    reviewSubmittedSuccess: false,
  };

  getTitle() {
    return this.props.title || this.props.csp?.title;
  }

  paginationTrackingDisplayStyle() {
    return this.props.trackingDisplayStyle === 'product' ? 'Item2CspReviewsSection' : 'CSP2CSPReviews';
  }

  @bind
  onSubmit(data) {
    let apiCall;

    if (this.myReview()) {
      apiCall = CoreClient.put(this.submitUrl(), data).then(async () => {
        await this.props.refetch();
        this.setState({ reviewSubmittedSuccess: true });
      });
    } else {
      apiCall = CoreClient.post(this.submitUrl(), data).then(async () => {
        await this.props.refetch();
        this.setState({ reviewSubmittedSuccess: true });
      });
    }

    return apiCall;
  }

  submitUrl() {
    if (this.myReview()) { return UrlHelpers.cspUpdateReviewUrl(this.myReview().id); }

    return UrlHelpers.cspReviewsUrl(this.props.csp.slug, null, null);
  }

  renderNoReviewsState() {
    return (
      <div className="csp-reviews__empty-state" data-empty-state>
        <h2 className="cms-h2 mtb-0">
          {I18n.t('discovery.cspReviewModule.noReviews')}
        </h2>
        <p className="mtb-0">
          {I18n.t('discovery.cspReviewModule.beTheFirst')}
        </p>
        <p className="mb-2">
          {I18n.t('discovery.cspReviewModule.writePrompt')}
        </p>
        <CSPReviewButton
          onSubmit={this.onSubmit}
          myReview={this.myReview()}
          productTitle={this.getTitle()}
          productImage={this.props.image}
          cspId={this.props.csp?.id}
          trackingDisplayStyle={this.props.trackingDisplayStyle}
        />
      </div>
    );
  }

  renderPrompt() {
    if (this.totalReviews() === 0) return null;
    return (
      <div className="csp-reviews__prompt" data-header-write-prompt>
        <small>
          {I18n.t('discovery.cspReviewModule.writePrompt')}
        </small>
        <div className="mt-half">
          <CSPReviewButton
            onSubmit={this.onSubmit}
            myReview={this.myReview()}
            productTitle={this.getTitle()}
            productImage={this.props.image}
            cspId={this.props.csp?.id}
            trackingDisplayStyle={this.props.trackingDisplayStyle}
          />
        </div>
      </div>
    );
  }

  renderStarRating() {
    return (
      <div className="d-flex fx-align-bottom mobile-d-inline-block">
        <RCStarRating
          rating={this.avgRating() / 5}
          count={this.totalReviews()}
          largeStars
        />
      </div>
    );
  }

  renderHeader() {
    return (
      <div className="csp-reviews__header">
        {!this.props.hideHeading &&
          <div className="fx-grow">
            <div className="csp-reviews__header__title" id="csp-review-title">
              {I18n.t('discovery.cspReviewModule.title', { title: this.getTitle() })}
            </div>
            {this.renderStarRating()}
          </div>
        }
        {this.renderPrompt()}
        <ReviewMetaData csp={this.props.csp} />
      </div>
    );
  }

  renderReview(review: ProductReview) {
    return (
      <CSPReview
        review={review}
        key={review.id}
        cspId={this.props.csp?.id}
        trackingDisplayStyle={this.props.trackingDisplayStyle}
      />
    );
  }

  renderSubmissionSuccess() {
    if (!this.state.reviewSubmittedSuccess) { return null; }

    return (
      <div className="scaling-mtb-2">
        <RCAlertBox type="success">
          {I18n.t('discovery.cspReviewModule.thankYouForReviewing')}
        </RCAlertBox>
      </div>
    );
  }

  truncateReview(index: number) {
    if (this.props.truncateAllReviews) return true;
    return index > 0;
  }

  myReview() { return this.props.csp.reviews.myReview; }

  productReviews() { return this.props.csp.reviews.reviews; }

  totalReviews() { return this.props.csp.reviews.total; }

  avgRating() { return this.props.csp.reviews.avgRating; }

  loading() { return !this.props.csp; }

  render() {
    // Always render the empty section for jump links
    return (
      <section
        className="csp-reviews"
        id="reviews-section"
      >
        {this.props.csp &&
          <div className="csp-reviews__inner">
            {this.renderSubmissionSuccess()}
            {!this.props.inCspCtaSidebarExperiment && this.renderHeader()}
            <Tiles
              oneWide
              emptyState={this.renderNoReviewsState()}
            >
              {this.props.csp.reviews.reviews.map((review, idx) => (
                <CSPReview
                  review={review}
                  key={review.id}
                  truncate={this.truncateReview(idx)}
                  cspId={this.props.csp?.id}
                  trackingDisplayStyle={this.props.trackingDisplayStyle}
                />
              ))}
            </Tiles>
            <RQLPagination
              limit={REVIEWS_LIMIT}
              total={this.props.csp.reviews.total}
              anchor="reviews-section"
              paginationWindow={2}
              pageParam="review_page"
              offset={this.props.offset}
              replaceHistory
              tracking={{ displayStyle: this.paginationTrackingDisplayStyle() }}
            />
          </div>
        }
      </section>
    );
  }
}

export const CSPReviewFragment = gql`
  fragment CSPReviewCSP on CSP {
    _id
    id
    title
    slug
  }

  fragment CSPReviews on core_apimessages_CSPReviews {
    total
    limit
    offset
    avgRating
    myReview {
      ...CspReviewData
    }
    reviews {
      ...CspReviewData
    }
  }

  fragment CspReviewData on core_apimessages_CSPReview {
    id
    body
    createdAt
    title
    rating
    voteCount
    reviewer {
      shortname
      links {
        avatar {
          href
        }
      }
    }
    verified
    blocked
    isMyReview
    didIVote
  }
`;
